import moment from "moment";
import { CustomHeader } from "tap/components/AGGrid/cell-renderers/CustomHeader.jsx";
import { ClickableCellRenderer } from "tap/components/AGGrid/cell-renderers/ClickableCellRenderer.jsx";
import { ExpandablePillboxList } from "tap/components/AGGrid/cell-renderers/ExpandablePillboxList.jsx";
import Button from "@dtci/spellbook/Button";
import Pillbox from "@dtci/spellbook/Pillbox";
import React from "react";
import { Link } from "react-router-dom";

export const statusIndicator = {
  headerName: "",
  field: "status",
  width: 20,
  cellRenderer: ({ value }) => (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        background: "#fff",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 2,
          top: 0,
          bottom: 0,
          width: "4px",
          background: getColorForStatus(value),
          borderRadius: "2px",
        }}
      />
      <span
        style={{
          marginLeft: "4px",
          background: getColorForStatus(value),
          display: "inline-block",
          width: "100%",
          height: "100%",
        }}
      >
        &nbsp;
      </span>
    </div>
  ),
  cellStyle: { padding: 0 },
  suppressHeaderMenuButton: true,
  sortable: false,
  filter: false,
};

export const view = (onOpenViewLicensingJob) => ({
  field: "",
  headerName: "",
  cellRenderer: ClickableCellRenderer(onOpenViewLicensingJob, "fa-eye"),
  width: 20,
});

export const edit = (onOpenEditLicensingJob) => ({
  field: "",
  headerName: "",
  cellRenderer: ClickableCellRenderer(
    onOpenEditLicensingJob,
    "fa-pencil",
    null,
    {},
    (data) => data.isTpr,
  ),
  width: 20,
});

export const id = {
  field: "id",
  headerName: "ID",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "id",
    page: "licensingJobs",
  },
  minWidth: 100,
};

export const description = {
  field: "description",
  headerName: "Description",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "description",
    page: "licensingJobs",
  },
  minWidth: 300,
};

export const catalogs = {
  field: "catalogs",
  headerName: "Catalogs",
  sortable: false,
  filter: false,
  minWidth: 500,
  flex: 1,
  valueFormatter: ({ value }) =>
    Array.isArray(value) ? value.map((c) => c.titleWithId).join(", ") : "",
  cellRenderer: ExpandablePillboxList,
  cellRendererParams: {
    labelField: "titleWithId",
    maxItems: 5,
    itemsPerRow: 3,
    preserveTrailingId: true,
    truncateLabels: true,
    expansionKeyPrefix: "catalogs",
  },
  autoHeight: true,
};

export const medias = {
  field: "medias",
  headerName: "Medias",
  sortable: false,
  filter: false,
  minWidth: 300,
  valueFormatter: ({ value }) =>
    Array.isArray(value)
      ? value
          .filter((m) => m?.name)
          .map((m) => m.name)
          .join(", ")
      : "",
  cellRenderer: ({ value }) => {
    const filtered = Array.isArray(value)
      ? value.filter((media) => media?.name)
      : [];
    if (filtered.length === 0) return null;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "8px 0",
        }}
      >
        {Array.from({ length: Math.ceil(filtered.length / 2) }, (_, i) => (
          <div key={i} style={{ display: "flex", gap: "10px" }}>
            {filtered.slice(i * 2, i * 2 + 2).map((media) => (
              <Pillbox
                badgeRole="listitem"
                key={media.id}
                color="primary"
                light
                showClose={false}
                size="small"
              >
                {media.name}
              </Pillbox>
            ))}
          </div>
        ))}
      </div>
    );
  },
};

export const territories = {
  field: "territories",
  headerName: "Territories",
  sortable: false,
  filter: false,
  minWidth: 300,
  flex: 1,
  valueFormatter: ({ value }) =>
    Array.isArray(value) ? value.map((t) => t.name).join(", ") : "",
  cellRenderer: ExpandablePillboxList,
  cellRendererParams: {
    labelField: "name",
    maxItems: 5,
    itemsPerRow: 3,
    expansionKeyPrefix: "territories",
  },
  autoHeight: true,
};

export const licenseeType = {
  field: "licenseeTypeList",
  headerName: "Licensee Type",
  sortable: false,
  filter: false,
  minWidth: 200,
  valueFormatter: ({ value }) =>
    Array.isArray(value) ? value.filter(Boolean).join(", ") : "",
  cellRenderer: ({ value }) => {
    const filtered = Array.isArray(value) ? value.filter((type) => type) : [];
    if (filtered.length === 0) return null;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          padding: "8px 0",
        }}
      >
        {Array.from({ length: Math.ceil(filtered.length / 2) }, (_, i) => (
          <div key={i} style={{ display: "flex", gap: "8px" }}>
            {filtered.slice(i * 2, i * 2 + 2).map((type, idx) => (
              <Pillbox
                badgeRole="listitem"
                key={type + idx}
                color="primary"
                light
                showClose={false}
                size="small"
              >
                {type}
              </Pillbox>
            ))}
          </div>
        ))}
      </div>
    );
  },
};
export const startDate = {
  field: "startDate",
  headerName: "Start Date",
  valueFormatter: ({ value }) =>
    value ? moment(value).format("DD-MMM-YYYY") : "",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "startDate",
    page: "licensingJobs",
  },
  minWidth: 150,
};

export const endDate = {
  field: "endDate",
  headerName: "End Date",
  valueFormatter: ({ value }) =>
    value ? moment(value).format("DD-MMM-YYYY") : "",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "endDate",
    page: "licensingJobs",
  },
  minWidth: 150,
};

export const isTpr = {
  field: "isTpr",
  headerName: "TPR",
  cellRenderer: ({ value }) => {
    if (value === true || value === "true") return "Yes";
    if (value === false || value === "false") return "No";
    return value ? "Yes" : "No";
  },
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "isTpr",
    page: "licensingJobs",
  },
  minWidth: 150,
};

export const run = (onRunLicensingJob) => ({
  field: "",
  headerName: "Run",
  valueGetter: () => "RUN",
  cellRenderer: ClickableCellRenderer(
    onRunLicensingJob,
    "fa-play",
    null,
    {},
    (data) => data?.status === "RUNNING" || data?.status === "RETRYING",
  ),
  minWidth: 125,
});

export const checkForUpdates = (onCheckForUpdates) => ({
  field: "",
  headerName: "Check for Updates",
  valueGetter: () => "UPDATES",
  cellRenderer: ClickableCellRenderer(
    onCheckForUpdates,
    "fa-play",
    null,
    {},
    (data) => data?.status === "RUNNING",
  ),
  minWidth: 125,
});

export const status = (onViewLicensingJobStatus) => ({
  field: "status",
  headerName: "Status",
  valueGetter: ({ data }) => {
    switch (data?.status) {
      case "QUEUED_UPDATED":
        return "QUEUED FOR UPDATES";
      case "QUEUED_UPDATED_USER_INITIATED":
        return "QUEUED FOR UPDATES USER INITIATED";
      case "QUEUED_UPDATED_ADMINISTRATOR":
        return "QUEUED FOR UPDATES ADMINISTRATOR";
      case "QUEUED_ADMINISTRATOR":
        return "QUEUED ADMINISTRATOR";
      case "RETRYING_AFTER_RESTART":
        return "RETRYING AFTER RESTART";
      case "RETRYING_UPDATES_AFTER_RESTART":
        return "RETRYING UPDATES AFTER RESTART";
      default:
        return data?.status;
    }
  },
  autoHeight: true,
  cellRenderer: ClickableCellRenderer(onViewLicensingJobStatus, "fa-eye"),
  minWidth: 150,
});

/**
 * AG Grid cell: navigate to Offers for this job. Uses Link so users can open in a new tab;
 * plain left-click prefetches via onViewOffers (same as legacy button behavior).
 * @param {Function} onViewOffers - Loads offer table, updates store, navigates in-app
 */
const OFFERS_LINK_ICON_TYPE = "fa-regular fa-eye";

const OffersLinkCellRenderer = (onViewOffers) => {
  const Renderer = ({ value, data, style = {} }) => {
    const isDisabled =
      data?.status === "RUNNING" || data?.status === "RETRYING";

    const mergedStyle = {
      color: isDisabled ? "#999" : "#3611c8",
      ...style,
      ...(isDisabled ? { cursor: "not-allowed", opacity: 0.6 } : {}),
    };

    if (isDisabled) {
      return (
        <Button
          style={mergedStyle}
          iconType={OFFERS_LINK_ICON_TYPE}
          type="button"
          styleType="link"
          className="sb-typography sb-typography_T20"
          disabled
        >
          {value}
        </Button>
      );
    }

    const to = `/offers?jobId=${data.id}`;

    return (
      <Link
        to={to}
        className="sb-button sb-button_primary sb-button_link sb-typography sb-typography_T20"
        style={mergedStyle}
        onContextMenu={(e) => e.stopPropagation()}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          if (e.button !== 0) return;
          e.preventDefault();
          onViewOffers(data);
        }}
      >
        <span className="sb-button_icon-container">
          <i
            className={`fas ${OFFERS_LINK_ICON_TYPE} sb-button_icon`}
            aria-hidden
          />
        </span>
        <span className="sb-button-text">{value}</span>
      </Link>
    );
  };

  return Renderer;
};

export const offers = (onViewOffers) => ({
  field: "",
  headerName: "Offers",
  valueGetter: ({ data }) =>
    data?.status === "RUNNING"
      ? "Pending"
      : `Offers (${data?.offerCount ?? 0})`,
  cellRenderer: OffersLinkCellRenderer(onViewOffers),
  minWidth: 150,
});

export const offerStatus = {
  field: "",
  headerName: "Offer Status",
  sortable: false,
  filter: false,
  minWidth: 300,
  autoHeight: true,
  cellRenderer: ({ data: record }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      <span>
        <span
          style={{ color: record.countOffersPending > 0 ? "green" : "black" }}
        >
          Pending ({record.countOffersPending})
        </span>
        {" / "}
        <span
          style={{ color: record.countOffersPushed > 0 ? "green" : "black" }}
        >
          Pushed ({record.countOffersPushed})
        </span>
      </span>
      <span
        style={{ color: record.countOffersSystemCreated > 0 ? "red" : "black" }}
      >
        System Created ({record.countOffersSystemCreated})
      </span>
      <span
        style={{
          color:
            record.countOffersUpdated + record.countOffersUserModified > 0
              ? "red"
              : "black",
        }}
      >
        Updated/Modified (
        {record.countOffersUpdated + record.countOffersUserModified})
      </span>
      <span
        style={{
          color:
            record.countOffersUpdatedAndActive +
              record.countOfferUserModifiedAndActive >
            0
              ? "red"
              : "black",
        }}
      >
        Active Updated/Modified (
        {record.countOffersUpdatedAndActive +
          record.countOfferUserModifiedAndActive}
        )
      </span>
      <span
        style={{ color: record.countOffersDuplicate > 0 ? "red" : "black" }}
      >
        Duplicate ({record.countOffersDuplicate})
      </span>
      <span
        style={{ color: record.countOffersNeedAttention > 0 ? "red" : "black" }}
      >
        Needs Attention ({record.countOffersNeedAttention})
      </span>
    </div>
  ),
};

export const latestJob = {
  field: "lastRun",
  headerName: "Latest Job",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "lastRun",
    page: "licensingJobs",
  },
  width: 100,
};

export const lastRunBy = {
  field: "startedBy.fullName",
  headerName: "Last Run By",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "startedBy.fullName",
    page: "licensingJobs",
  },
  width: 150,
};

export const completedAt = {
  field: "completedAt",
  headerName: "Completed",
  valueFormatter: ({ value }) =>
    value
      ? moment(moment.utc(value).toDate())
          .tz(moment.tz.guess())
          .format("DD-MMM-YYYY, h:mm:ss A z")
      : "",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "completedAt",
    page: "licensingJobs",
  },
  minWidth: 250,
};

export const createdBy = {
  field: "createdBy.fullName",
  headerName: "Created By",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "createdBy.fullName",
    page: "licensingJobs",
  },
  width: 150,
};

export const createdAt = {
  field: "createdAt",
  headerName: "Created",
  valueFormatter: ({ value }) =>
    value
      ? moment(moment.utc(value).toDate())
          .tz(moment.tz.guess())
          .format("DD-MMM-YYYY, h:mm:ss A z")
      : "",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "createdAt",
    page: "licensingJobs",
  },
  minWidth: 250,
};

export const updatedBy = {
  field: "modifiedBy.fullName",
  headerName: "Updated By",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "modifiedBy.fullName",
    page: "licensingJobs",
  },
  width: 150,
};

export const modifiedAt = {
  field: "modifiedAt",
  headerName: "Modified",
  valueFormatter: ({ value }) =>
    value
      ? moment(moment.utc(value).toDate())
          .tz(moment.tz.guess())
          .format("DD-MMM-YYYY, h:mm:ss A z")
      : "",
  headerComponent: CustomHeader,
  headerComponentParams: {
    colId: "modifiedAt",
    page: "licensingJobs",
  },
  minWidth: 250,
};

export const copy = (onCopyLicensingJob) => ({
  field: "",
  headerName: "",
  valueGetter: () => "COPY",
  cellRenderer: ClickableCellRenderer(
    onCopyLicensingJob,
    "fa-copy",
    null,
    {},
    (data) => data.isTpr,
  ),
  minWidth: 100,
});

export const inactivate = (onInactivateLicensingJob) => ({
  field: "",
  headerName: "",
  valueGetter: () => "INACTIVATE",
  cellRenderer: ClickableCellRenderer(
    onInactivateLicensingJob,
    "fa-trash",
    null,
    {},
    (data) => data.isTpr,
  ),
  cellRendererParams: {
    style: { color: "red" },
  },
  minWidth: 125,
});

// MUI palette values
const green500 = "#4caf50";
const orange500 = "#ffa500";
const red500 = "#f44336";
const blue500 = "#2196f3";

function getColorForStatus(status, record = {}) {
  const s = (status || record.status || "").toUpperCase();

  if (s === "ACTIVE") return green500;
  if (s === "NEEDS_ATTENTION" || record.countOffersUpdatedAndActive > 0)
    return orange500;
  if (
    s === "ERROR" ||
    s === "INACTIVE" ||
    s === "INACTIVATED" ||
    s === "ABORTED" ||
    s === "INVALID" ||
    record.countOffersInvalid > 0
  )
    return red500;
  if (s === "COMPLETE") return green500;
  if (s === "READY" || s === "CREATED") return green500;
  if (s === "STARTING" || s === "RUNNING" || s === "VALIDATING") return blue500;
  if (s === "FAILED" || s === "INVALID") return red500;

  return "#ccc";
}
